import { useLayoutEffect } from "react";

/**
 * Single source of truth for "how tall is the sticky chrome?". Attach to the
 * sticky Navbar or AdminLayout header element via a ref; this hook measures
 * it with a ResizeObserver and exposes the height on the `<html>` element as
 * `--app-nav-height`. Any fixed / sticky element downstream can consume the
 * value through `var(--app-nav-height)` or the Tailwind utilities `.top-nav`
 * and `.h-below-nav` (defined in src/index.css).
 *
 * Only one chrome surface is mounted at a time (consumer Navbar vs.
 * AdminLayout header, App.tsx switches via `isAdminPath`), so a single
 * variable correctly tracks whichever is on screen. The default is `0px`,
 * which matches the embed routes where no chrome renders.
 */
export const useExposeNavHeight = (ref: React.RefObject<HTMLElement>) => {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;

    const apply = (h: number) => {
      root.style.setProperty("--app-nav-height", `${Math.round(h)}px`);
    };

    apply(el.getBoundingClientRect().height);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      apply(entry.contentRect.height + (entry.contentRect.top * 0));
      // contentRect excludes padding-box additions in some browsers; fall
      // back to the bounding-rect height which is always the full chrome.
      apply(el.getBoundingClientRect().height);
    });
    observer.observe(el);

    return () => {
      observer.disconnect();
      root.style.setProperty("--app-nav-height", "0px");
    };
  }, [ref]);
};
